import type { DocumentType, GFADocumentType } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	UploadMultipleFiles,
} from "@dune/ui";
import {
	documentTypeLabels,
	documentTypes,
	gfaDocumentTypeLabels,
	gfaDocumentTypes,
} from "@dune/vars";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { useUploadConstructionSiteDocumentMutation } from "../../../../api/mutations/constructionSites.mutations";
import { useLinkFileToGFA } from "../../../../api/mutations/gfa.mutations";
import {
	getSignedPutUrl,
	registerS3File,
	uploadFileToS3,
} from "../../../../api/queries/files.queries";

interface UploadDocumentModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionSiteId: number;
	isGFA?: boolean;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
	open,
	onOpenChange,
	constructionSiteId,
	isGFA = false,
}) => {
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [documentType, setDocumentType] = useState<
		GFADocumentType | DocumentType | null
	>(null);
	const [isUploadingFile, setIsUploadingFile] = useState(false);

	const uploadDocumentMutation = useUploadConstructionSiteDocumentMutation();
	const linkFileToGFAMutation = useLinkFileToGFA(
		isGFA ? constructionSiteId : 0,
		{ invalidateDocuments: true },
	);

	const availableDocumentTypes = useMemo(() => {
		return isGFA ? gfaDocumentTypes : documentTypes;
	}, [isGFA]);

	const getDocumentTypeLabel = (type: string): string => {
		const labels = isGFA ? gfaDocumentTypeLabels : documentTypeLabels;
		return labels[type as keyof typeof labels] ?? type;
	};

	const handleUpload = async () => {
		if (selectedFiles.length === 0 || !documentType) {
			toast.error(
				"Veuillez sélectionner au moins un fichier et un type de document",
			);
			return;
		}

		try {
			setIsUploadingFile(true);

			for (const file of selectedFiles) {
				const signedUrl = await getSignedPutUrl({
					bucket: "private",
					contentType: file.type,
				});
				await uploadFileToS3(signedUrl.signedUrl, file);

				if (isGFA) {
					const registeredFile = await registerS3File({
						bucket: "private",
						key: signedUrl.key,
						name: file.name,
						mimeType: file.type,
						size: file.size,
					});
					await linkFileToGFAMutation.mutateAsync({
						fileId: registeredFile.id,
						type: documentType as GFADocumentType,
					});
				} else {
					await uploadDocumentMutation.mutateAsync({
						constructionSiteId,
						name: file.name,
						key: signedUrl.key,
						bucket: "private",
						mimeType: file.type,
						type: documentType as DocumentType,
					});
				}
			}

			const successMessage =
				selectedFiles.length > 1
					? `${selectedFiles.length} documents uploadés avec succès`
					: "Document uploadé avec succès";
			toast.success(successMessage);
			onOpenChange(false);
			setSelectedFiles([]);
			setDocumentType(null);
		} catch (error) {
			console.error("Upload failed:", error);
		} finally {
			setIsUploadingFile(false);
		}
	};

	const handleModalClose = (newOpen: boolean) => {
		if (!newOpen) {
			setSelectedFiles([]);
			setDocumentType(null);
		}
		onOpenChange(newOpen);
	};

	const buttonLabel =
		selectedFiles.length > 1
			? `Ajouter les ${selectedFiles.length} documents`
			: "Ajouter le document";

	return (
		<Dialog open={open} onOpenChange={handleModalClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Ajouter un document</DialogTitle>
					<DialogDescription>
						Sélectionnez un ou plusieurs fichiers et choisissez le type de
						document
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<div className="text-sm font-medium mb-2">Type de document</div>
						<Select
							value={documentType ?? ""}
							onValueChange={(value) =>
								setDocumentType(value as GFADocumentType | DocumentType)
							}
						>
							<SelectTrigger>
								<SelectValue placeholder="Sélectionnez un type de document" />
							</SelectTrigger>
							<SelectContent>
								{availableDocumentTypes.map((option) => (
									<SelectItem key={option} value={option}>
										{getDocumentTypeLabel(option)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div>
						<div className="text-sm font-medium mb-2">Fichier(s)</div>
						<UploadMultipleFiles
							key={`upload-modal-${open}`}
							onFilesChange={setSelectedFiles}
							subText="Formats acceptés : PDF, DOCX, PNG, JPG (max. 10MB)"
						/>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleUpload}
						disabled={
							selectedFiles.length === 0 ||
							!documentType ||
							uploadDocumentMutation.isPending ||
							linkFileToGFAMutation.isPending ||
							isUploadingFile
						}
						isLoading={
							uploadDocumentMutation.isPending ||
							linkFileToGFAMutation.isPending ||
							isUploadingFile
						}
					>
						{buttonLabel}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default UploadDocumentModal;
